import { mergeAtSeam, splitPiece } from "@/components/CountertopDesigner";
import { api } from "@/lib/api";
import { shrinkForUpload } from "@/lib/shrinkImage";

export const uid = () => Math.random().toString(36).slice(2, 10);

// sketch / photo underlay for the drawing: stored with the layout photos
export const uploadTracePhoto = async (file) => {
  const fd = new FormData();
  fd.append("file", await shrinkForUpload(file));
  const { data } = await api.post("/quotes/layout-photo", fd);
  return data.storage_path;
};

export const newArea = (n) => ({
  id: uid(),
  name: `Area ${n}`,
  address: "",
  pieces: [],
  cutouts: [],
});

export const movePieceIn = (cur, pieceId, targetAreaId) => {
  const piece = cur.areas
    .flatMap((a) => a.pieces || [])
    .find((p) => p.id === pieceId);
  if (!piece) return cur;
  return {
    ...cur,
    areas: cur.areas.map((a) =>
      a.id === targetAreaId
        ? { ...a, pieces: [...(a.pieces || []), piece] }
        : { ...a, pieces: (a.pieces || []).filter((p) => p.id !== pieceId) },
    ),
  };
};

// null when the cut misses the piece
export const splitAreaPieceIn = (cur, areaId, pieceId, axis, at) => {
  const area = cur.areas.find((a) => a.id === areaId);
  const piece = (area?.pieces || []).find((p) => p.id === pieceId);
  if (!piece) return cur;
  const halves = splitPiece(piece, axis, at);
  if (!halves) return null;
  const layout = cur.layout || {};
  const placements = { ...(layout.placements || {}) };
  const old = placements[pieceId];
  delete placements[pieceId];
  if (old) {
    placements[halves[0].id] = { ...old };
    placements[halves[1].id] = {
      ...old,
      x: axis === 0 ? old.x + at + 1 : old.x,
      y: axis === 1 ? old.y + at + 1 : old.y,
    };
    // a half that ends up entirely off its slab moves to the next slab (or the unplaced pile)
    const slabs = layout.slabs || [];
    const s = slabs[old.slab ?? 0];
    if (s)
      halves.forEach((h) => {
        const pl = placements[h.id];
        const swap = Math.abs(((pl.rot || 0) % 180) + 180) % 180 === 90;
        const w = swap ? h.width_in : h.length_in;
        const hh = swap ? h.length_in : h.width_in;
        const off =
          pl.x + w <= 0.01 || pl.x >= s.length_in - 0.01 || pl.y + hh <= 0.01 || pl.y >= s.width_in - 0.01;
        if (!off) return;
        const nextSlab = slabs.findIndex((_, i) => i !== (old.slab ?? 0) && i > (old.slab ?? 0));
        if (nextSlab >= 0) placements[h.id] = { ...pl, slab: nextSlab, x: 0, y: 0 };
        else delete placements[h.id];
      });
  }
  return {
    ...cur,
    layout: { ...layout, placements },
    areas: cur.areas.map((a) =>
      a.id !== areaId
        ? a
        : {
            ...a,
            pieces: a.pieces.flatMap((p) => (p.id === pieceId ? halves : [p])),
          },
    ),
  };
};

// Remove the seam on a split piece: weld it back onto the neighbour it was cut from.
export const mergeSeamIn = (cur, areaId, pieceId) => {
  const area = cur.areas.find((a) => a.id === areaId);
  const piece = (area?.pieces || []).find((p) => p.id === pieceId);
  if (!piece) return null;
  const others = area.pieces.filter((p) => p.id !== pieceId);
  let res = null;
  (piece.edges || []).forEach((e, i) => {
    if (!res && e.seam) res = mergeAtSeam(piece, i, others);
  });
  if (!res) return null;
  const layout = cur.layout || {};
  const placements = { ...(layout.placements || {}) };
  delete placements[res.removeId];
  return {
    ...cur,
    layout: { ...layout, placements },
    areas: cur.areas.map((a) =>
      a.id !== areaId
        ? a
        : {
            ...a,
            pieces: a.pieces
              .filter((p) => p.id !== res.removeId)
              .map((p) => (p.id === pieceId ? res.piece : p)),
          },
    ),
  };
};

export const materialOptionsFor = (slabStock, remnants, colors) => [
  ...Object.values(
    slabStock.reduce((acc, s) => {
      const key = `${s.slab_color} ${s.thickness}`;
      if (!acc[key])
        acc[key] = {
          value: `slab|${s.slab_color}|${s.stone_group || ""}|${s.thickness || ""}`,
          label: `${s.slab_color} ${s.thickness}${
            s.stone_group ? ` · ${s.stone_group}` : ""
          }`,
          hint: "In stock",
        };
      return acc;
    }, {}),
  ),
  ...remnants.map((r) => ({
    value: `remnant|${r.slab_color}||${r.thickness || ""}`,
    label: `${r.slab_color} ${r.thickness} · ${r.code} · ${r.usable_sqft || 0} ft²`,
    hint: "Remnant",
  })),
  ...colors
    .filter(
      (c) =>
        !slabStock.some(
          (s) => s.slab_color === c.slab_color && s.thickness === c.thickness,
        ),
    )
    .map((c) => ({
      value: `catalog|${c.slab_color}||${c.thickness || ""}`,
      label: `${c.slab_color} ${c.thickness || ""}`.trim(),
      hint: "Catalogue",
    })),
];

export const areaMaterialPatch = (value) => {
  const [kind, color, group] = String(value || "").split("|");
  if (!kind || !color) return { slab_color: value || "" };
  if (kind === "catalog") return { slab_color: color };
  return { slab_color: color, ...(group ? { stone_group: group } : {}) };
};

export const materialValueFor = (area, quote, options) => {
  const color = area.slab_color || quote.slab_color || "";
  if (!color) return "";
  const hit = options.find((o) => o.value.split("|")[1] === color);
  return hit ? hit.value : color;
};
